'use client'

import {FC, useEffect, useRef} from 'react'

import {analytics} from '@/utils/tracking/analytics'

import {usePreferences} from './Preferences'

/**
 * Keeps the analytics super-properties in sync with the current preferences, and
 * emits an event when they actually change.
 *
 * Side-effect only, so it lives outside `PreferencesProvider` rather than inside it
 * — same pattern as `SessionChecker`.
 */
export const PreferencesAnalytics: FC = () => {
  const {origin, destination, country, currency, affiliate, isPreferencesPending} = usePreferences()
  const isInitialRun = useRef(true)

  useEffect(() => {
    // A statically rendered page holds the defaults until it has resolved; registering those would
    // file every visitor under region US for a moment, and their replacement as a change
    if (isPreferencesPending) {
      return
    }
    const preferences = {origin, destination, country, currency, affiliate}

    // Super-properties are state rather than an event: they have to be registered on
    // mount too, so every subsequent event carries them.
    analytics.register(preferences)

    // The event means "the user changed something". Firing it on mount as well would
    // emit one per page load, which is just a worse page view.
    if (isInitialRun.current) {
      isInitialRun.current = false
      return
    }

    analytics.track('Preferences update', preferences)
  }, [origin, destination, country, currency, affiliate, isPreferencesPending])

  return null
}
